'use client';

import { PbValues } from './types';
import { ToolsTranslations } from '@/lib/translations/types';

interface CalculatorInputsProps {
  values: PbValues;
  onValuesChange: (field: keyof PbValues, value: number | string) => void;
  translations: ToolsTranslations['pb'];
}

export default function CalculatorInputs({ values, onValuesChange, translations }: CalculatorInputsProps) {
  const handleSliderChange = (field: keyof PbValues, value: number) => {
    onValuesChange(field, value);
  };

  const handleInputChange = (field: keyof PbValues, value: string) => {
    if (field === 'currency') {
      onValuesChange(field, value);
      return;
    }
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0) {
      onValuesChange(field, numValue);
    }
  };

  return (
    <div className="space-y-6">
      {/* Market Price per Share */}
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <svg 
              className="w-4 h-4 text-blue-500 animate-pulse" 
              fill="currentColor" 
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path fillRule="evenodd" d="M16.707 10.293a1 1 0 010 1.414l-6 6a1 1 0 01-1.414 0l-6-6a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l4.293-4.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <label htmlFor="market-price-input" className="text-gray-700 font-medium">{translations.marketPricePerShare}</label>
          </div>
          <div className="flex items-center gap-2">
            <input
              id="market-price-input"
              type="text"
              value={values.marketPricePerShare.toLocaleString('sv-SE')}
              onChange={(e) => handleInputChange('marketPricePerShare', e.target.value.replace(/\s/g, ''))}
              className="w-32 text-right border-b-2 border-gray-300 focus:border-blue-500 focus:outline-none focus:ring-0 px-1 py-1 text-gray-800 font-semibold bg-transparent"
              aria-label={translations.marketPricePerShare}
            />
            <span className="text-gray-800 font-semibold">{values.currency}</span>
          </div>
        </div>
        <div className="relative">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-green-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${Math.min((values.marketPricePerShare / 1000) * 100, 100)}%` }}
            ></div>
          </div>
          <input
            id="market-price-slider"
            type="range"
            min="1"
            max="1000"
            step="0.1"
            value={values.marketPricePerShare}
            onChange={(e) => handleSliderChange('marketPricePerShare', parseFloat(e.target.value))}
            className="absolute inset-0 w-full h-2 cursor-pointer slider"
            aria-label={`${translations.marketPricePerShare} slider`}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-500">
          <span>1 {values.currency}</span>
          <span>1000 {values.currency}</span>
        </div>
      </div>

      {/* Book Value per Share */}
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <svg 
              className="w-4 h-4 text-blue-500 animate-pulse" 
              fill="currentColor" 
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path fillRule="evenodd" d="M16.707 10.293a1 1 0 010 1.414l-6 6a1 1 0 01-1.414 0l-6-6a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l4.293-4.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <label htmlFor="book-value-input" className="text-gray-700 font-medium">{translations.bookValuePerShare}</label>
          </div>
          <div className="flex items-center gap-2">
            <input
              id="book-value-input"
              type="text"
              value={values.bookValuePerShare.toLocaleString('sv-SE')}
              onChange={(e) => handleInputChange('bookValuePerShare', e.target.value.replace(/\s/g, ''))}
              className="w-32 text-right border-b-2 border-gray-300 focus:border-blue-500 focus:outline-none focus:ring-0 px-1 py-1 text-gray-800 font-semibold bg-transparent"
              aria-label={translations.bookValuePerShare}
            />
            <span className="text-gray-800 font-semibold">{values.currency}</span>
          </div>
        </div>
        <div className="relative">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-green-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${Math.min((values.bookValuePerShare / 500) * 100, 100)}%` }}
            ></div>
          </div>
          <input
            id="book-value-slider"
            type="range"
            min="1"
            max="500"
            step="0.1"
            value={values.bookValuePerShare}
            onChange={(e) => handleSliderChange('bookValuePerShare', parseFloat(e.target.value))}
            className="absolute inset-0 w-full h-2 cursor-pointer slider"
            aria-label={`${translations.bookValuePerShare} slider`}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-500">
          <span>1 {values.currency}</span>
          <span>500 {values.currency}</span>
        </div>
      </div>

      <style jsx>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: #10b981;
          cursor: pointer;
          border: 2px solid #ffffff;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
        
        .slider::-moz-range-thumb {
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: #10b981;
          cursor: pointer;
          border: 2px solid #ffffff;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
      `}</style>
    </div>
  );
}
